import { useState } from "react";
import axios from "axios";
import { FileUp, Loader2, Zap } from "lucide-react";
import { RUSH_PRICE } from "./content";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.doc,.docx";
const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-base text-white placeholder:text-slate-500 transition-colors focus:border-[var(--teal)] focus:outline-none";

const errorText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm text-slate-300">{label}</span>
    {children}
  </label>
);

export const OrderDialog = ({ tier, open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", target_job: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rush, setRush] = useState(false);
  const total = (tier?.price || 0) + (rush ? RUSH_PRICE : 0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError("Please attach your resume.");
    if (file.size > 10 * 1024 * 1024) return setError("File must be under 10 MB.");
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      body.append("lookup_key", tier.lookup);
      body.append("rush", rush ? "true" : "false");
      body.append("resume", file);
      const { data } = await axios.post(`${API}/orders`, body);
      const res = await axios.post(`${API}/payments/checkout`, { order_id: data?.order_id, origin_url: window.location.origin });
      if (!res.data?.checkout_url) throw new Error("no url");
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-lenis-prevent data-testid="order-dialog"
        className="max-h-[92vh] max-w-lg overflow-y-auto border-white/10 bg-[var(--navy)] p-6 text-white sm:rounded-3xl sm:p-8 [&>button]:text-slate-300">
        <DialogHeader>
          <DialogTitle className="font-serif-d text-4xl font-normal">{tier?.name} · ${tier?.price}</DialogTitle>
          <DialogDescription className="text-slate-300">Tell us where you're headed and attach your resume. Then you'll pay securely by card or bank transfer.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="order-form">
          <Field label="Your name"><input required className={field} value={form.name} onChange={set("name")} data-testid="order-name-input" /></Field>
          <Field label="Your email"><input required type="email" className={field} value={form.email} onChange={set("email")} data-testid="order-email-input" /></Field>
          <Field label="Target job type">
            <input required className={field} placeholder="e.g. Operations Manager in logistics" value={form.target_job} onChange={set("target_job")} data-testid="order-target-input" />
          </Field>
          <Field label="Anything we should know? (optional)">
            <textarea rows={3} className={field} placeholder="A job posting link, roles to focus on…" value={form.notes} onChange={set("notes")} data-testid="order-notes-input" />
          </Field>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[var(--teal)]/50 px-4 py-4 transition-colors hover:bg-white/5" data-testid="order-file-dropzone">
            <FileUp className="h-5 w-5 shrink-0 text-[var(--teal)]" />
            <span className="min-w-0 flex-1 truncate text-sm text-slate-200">{file ? file.name : "Attach resume (PDF or Word, max 10 MB)"}</span>
            <input type="file" accept={ACCEPT} className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="order-file-input" />
          </label>
          <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-4 transition-colors ${rush ? "border-[var(--amber)] bg-[var(--amber)]/10" : "border-white/15 hover:bg-white/5"}`} data-testid="order-rush-option">
            <input type="checkbox" checked={rush} onChange={(e) => setRush(e.target.checked)} className="mt-1 h-4 w-4 accent-[#f2b84b]" data-testid="order-rush-checkbox" />
            <span className="flex-1">
              <span className="flex items-center gap-1.5 font-semibold text-white"><Zap className="h-4 w-4 text-[var(--amber)]" /> Rush delivery <span className="ml-auto font-normal text-[var(--amber)]">+${RUSH_PRICE}</span></span>
              <span className="mt-0.5 block text-sm text-slate-300">Finished files within 24 hours of payment.</span>
            </span>
          </label>
          {error && <p className="text-sm text-[var(--amber)]" role="alert" data-testid="order-error">{error}</p>}
          <p className="flex items-center justify-between border-t border-white/10 pt-4 text-sm text-slate-300" data-testid="order-total">
            <span>Total · card or bank transfer · promo codes at payment</span><span className="font-serif-d text-3xl text-white">${total}</span>
          </p>
          <button type="submit" disabled={busy} data-testid="order-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--teal)] px-6 py-4 font-semibold text-[var(--ink)] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-70">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <>Continue to payment</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
